import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
}                             from 'react-query';
import { AxiosError, Method } from 'axios';
import { callApi }            from '@utils/apiCaller';
import { Notification }       from '@components/Notification/Notification';
import { showErrorMessage }   from '@utils/commonFunctions';
import {
  ICardAd,
  IBannerAd,
  ICardAdValues,
  IBannerAdValues,
}                             from '@typings/nationalAdManagement';

// #region FETCH CARD ADS
const fetchCardAds = async (): Promise<ICardAd[]> => {
  const { data } = await callApi<ICardAd[]>('AdManager/my-ads');

  return data;
};

export const useFetchCardAds = (
  config?: UseQueryOptions<ICardAd[], AxiosError>,
): UseQueryResult<ICardAd[], AxiosError> => useQuery(
  ['card-ads'],
  fetchCardAds,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH CARD AD
const fetchCardAd = async (id: string): Promise<ICardAd> => {
  try {
    const { data } = await callApi<ICardAd>(`AdManager/ad/${id}`);

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useFetchCardAd = (
  id      : string,
  config? : UseQueryOptions<ICardAd, AxiosError>,
): UseQueryResult<ICardAd, AxiosError> => useQuery(
  ['card-ad', id],
  () => fetchCardAd(id),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region SAVE CARD AD
const saveCardAd = async (values: ICardAdValues): Promise<ICardAd> => {
  try {
    const { id } = values;

    const endpoint : string = id ? `AdManager/update-ad?adId=${id}` : 'AdManager/create-ad';
    const method   : Method = id ? 'put' : 'post';
    const message  : string = id ? 'The ad has been updated' : 'The ad has been created';

    const formData = new FormData();
    const keys     = Object.keys(values) as Array<keyof ICardAdValues>;

    keys.forEach(
      (key): void => {
        const value = key === 'image' ? values[key] : values[key]?.toString();

        if (!value) {
          return;
        }

        formData.append(key, value);
      },
    );

    const { data } = await callApi<ICardAd>(
      endpoint,
      method,
      formData,
      { isFormData: true },
    );

    Notification.enqueueSnackbar(message, 'info');

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useSaveCardAd = (
  config: UseMutationOptions<ICardAd, AxiosError, ICardAdValues> = {},
): UseMutationResult<ICardAd, AxiosError, ICardAdValues> => useMutation(
  saveCardAd,
  config,
);
// #endregion

// #region REMOVE CARD AD
const removeCardAd = async (adId: string): Promise<void> => {
  try {
    await callApi(
      'AdManager/remove-ad',
      'delete',
      { adId },
    );

    Notification.enqueueSnackbar(
      'The ad has been deleted',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useRemoveCardAd = (
  config: UseMutationOptions<void, AxiosError, string> = {},
): UseMutationResult<void, AxiosError, string> => useMutation(
  removeCardAd,
  config,
);
// #endregion

// #region FETCH BANNER
const fetchBannerAd = async (): Promise<IBannerAd> => {
  const { data } = await callApi<IBannerAd>('AdManager/banner');

  return data;
};

export const useFetchBannerAd = (
  config?: UseQueryOptions<IBannerAd, AxiosError>,
): UseQueryResult<IBannerAd, AxiosError> => useQuery(
  ['banner'],
  fetchBannerAd,
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region SAVE BANNER
const saveBannerAd = async (payload: IBannerAdValues): Promise<IBannerAd> => {
  try {
    const message: string = payload.id ? 'Banner has been updated.' : 'Banner has been saved.';

    const { data } = await callApi<IBannerAd>(
      'AdManager/banner',
      'post',
      payload,
    );

    Notification.enqueueSnackbar(message, 'info');

    return data;
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useSaveBannerAd = (
  config: UseMutationOptions<IBannerAd, AxiosError, IBannerAdValues> = {},
): UseMutationResult<IBannerAd, AxiosError, IBannerAdValues> => useMutation(
  saveBannerAd,
  config,
);
// #endregion
